import { useNavigate, useOutletContext } from 'react-router-dom'

import { F0Alert, F0Box, F0Heading } from '@factorialco/f0-react'
import { type PlanningPeriodDetailQuery } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'
import DirectReporteesTable from 'modules/planning/components/DirectReporteesTable'
import OverviewSummaryCards from 'modules/planning/components/OverviewSummaryCards'
import { PLANNING_PERIOD_STATUS } from 'modules/planning/consts/period_status'
import { getCapabilities, type PeriodRoleContext } from 'modules/planning/roles'

type PeriodDetailContext = {
  period: PlanningPeriodDetailQuery['planning']['period']
  periodId: string
  roleContext: PeriodRoleContext | undefined
}

const PeriodOverviewTab = () => {
  const { period, periodId, roleContext } = useOutletContext<PeriodDetailContext>()
  const { company } = useCurrentSession()
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const currency =
    company.legalEntities?.find((entity) => entity.main)?.currency ?? company.currency ?? 'EUR'

  const capabilities = getCapabilities(roleContext?.roles ?? [])
  const isToBeReviewed = period?.status === PLANNING_PERIOD_STATUS.TO_BE_REVIEWED
  const isApprover = capabilities.has('approveRejectRequest')
  const showReviewBanner = isApprover && isToBeReviewed
  const showUnderReviewBanner = !isApprover && isToBeReviewed

  const handleReviewRequests = () => {
    const employeeSheetPath =
      navigation.finance.financialWorkspace.headcountPlanning.periodDetail.employeeSheet.fullPath({
        periodId,
      })
    navigate(employeeSheetPath)
  }

  return (
    <F0Box display="flex" flexDirection="column" gap="3xl" paddingTop="md">
      {showReviewBanner && (
        <F0Alert
          variant="warning"
          title={i18n.t('planning.overview.review_banner.title')}
          description={i18n.t('planning.overview.review_banner.description')}
          action={{
            label: i18n.t('planning.overview.review_banner.action'),
            onClick: handleReviewRequests,
          }}
        />
      )}
      {showUnderReviewBanner && (
        <F0Alert
          variant="info"
          title={i18n.t('planning.overview.under_review_banner.title')}
          description={i18n.t('planning.overview.under_review_banner.description')}
        />
      )}
      <OverviewSummaryCards periodId={periodId} currency={currency} />
      <F0Box display="flex" flexDirection="column" gap="md">
        <F0Heading content={i18n.t('planning.overview.direct_reportees')} variant="heading" />
        <DirectReporteesTable periodId={periodId} currency={currency} />
      </F0Box>
    </F0Box>
  )
}

export default PeriodOverviewTab
